import React, { FC, useEffect, useMemo, useState } from "react";
import { CounterBadge, Menu, MenuDivider, MenuGroup, MenuGroupHeader, MenuItem, MenuList, MenuPopover, MenuTrigger, Tab, TabList } from "@fluentui/react-components";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
  useSearchParams
} from "react-router-dom";
import {
  BriefcaseSearchFilled,
    BriefcaseSearchRegular,
    bundleIcon,
    ClipboardDataBarFilled,
    ClipboardDataBarRegular,
    DocumentBulletListMultipleFilled,
    DocumentBulletListMultipleRegular,
  DocumentBulletListRegular,
  HomeFilled,
  HomeRegular,
  ListBarTreeOffsetRegular,
  NotebookFilled,
  NotebookRegular,
  PeopleAudienceRegular,
  SearchRegular,
  WindowShieldFilled,
  WindowShieldRegular
} from "@fluentui/react-icons";
import Start from "./Start";
import Placement from "./Placements";
import Tendering from "./Tendering";
import { useAppContext } from "../AppContext";
import Login from "./login"
import authService from "../services/authService";
import { Module } from "../models/module";
import TenderChanges from "./TenderChanges";
import TenderingFeeTool from "./TenderingFeeTool";
import PlacementComponent from "./Placement/Placement";
import PlacementRequestComponent from "./Placement/PlacementRequestComponent";
import TenderSettingsDrawer from "./TenderSettings/TenderSettingsDrawer";
import AccountRequest from "./AccountRequest";
import NewsDrawer from "./drawers/NewsDrawer";
import { RoleDTO } from "../models/role";
import TenderingManagerTool from "./TenderingManagerTool";
import UserGroupRequests from "./drawers/UserGroupRequests";
import TenderingRequestDialog from "./dialogs/TenderingRequestDialog";
import NotificationDialog from "./dialogs/NotificationDialog";
import ErrorDialog from "./dialogs/ErrorDialog";
import { CreateTask, Task, TaskData, TaskListItem, TENDERING_ACCESS_REQUEST_TASK_TYPE_ID } from "../models/task";
import { createTenderingRequestTask, getTasks } from "../services/taskService";
import { getUserGroupsByModuleAndOrganizationID } from "../services/userGroupService";
import { getUsersByModuleAndRole } from "../services/usersService";
import UserReset from "./UserReset";
import { set } from "react-hook-form";
import UserGroupsDrawer from "./drawers/UserGroupsDrawer";
import { LogsTool } from "./LogsTool";
import SessionWarningBanner from "./SessionWarningBanner";

type TabValue = "tab1" | "tab2" | "tab3" | "tab4" | "tab5" | "tab6" | "tab7" | "tab8" | "tab9" | "tab10";
const HomeIcon = bundleIcon(HomeFilled, HomeRegular);
const DocumentBulletListIcon = bundleIcon(DocumentBulletListMultipleFilled, DocumentBulletListMultipleRegular);
const BriefcaseSearchIcon = bundleIcon(BriefcaseSearchFilled, BriefcaseSearchRegular);
const ClipboardDataBarIcon = bundleIcon(ClipboardDataBarFilled, ClipboardDataBarRegular);
const WindowShieldIcon = bundleIcon(WindowShieldFilled, WindowShieldRegular );
const NoteBookIcon = bundleIcon(NotebookFilled, NotebookRegular);

const Main: FC = () => {
  const [actualTab, setActualTab] = useState<TabValue>('tab1');
  const [tenderingModules, setTenderingModules] = useState<Module[]>([]);
  const [tenderModules, setTenderModules] = useState<Module[]>([]);
  const [selectedModule, setSelectedModule] = useState<Module>();
  const [selectedTenderModule, setSelectedTenderModule] = useState<Module>();
  const [openTenderSettingsDrawer, setOpenTenderSettingsDrawer] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<{ [key: number]: boolean }>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tenderingMenuOpen, setTenderingMenuOpen] = useState<boolean>(false);
  const {user, setUser, featurePermissions, userTasks, hasTenderingPageAccess} = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] = useState<boolean>(false);
  const [notificationText, setNotificationText] = useState<string>("");
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [pendingTenderingRequest, setPendingTenderingRequest] = useState<Module | undefined>(undefined);
  const [tenderingRequestUserGroups, setTenderingRequestUserGroups] = useState<{ userGroupID: number; userGroupName: string; userGroupAssignments?: any[] }[]>([]);
  const [selectedRequestUserGroupIDs, setSelectedRequestUserGroupIDs] = useState<string[]>([]);
  const [requestUserGroupAdminsMap, setRequestUserGroupAdminsMap] = useState<Record<string, { userID: number; email: string; firstName: string; lastName: string }[]>>({});
  const [loadingRequestUserGroupAdminIDs, setLoadingRequestUserGroupAdminIDs] = useState<string[]>([]);
  const [requestTasks, setRequestTasks] = useState<TaskListItem[]>([]);
  const [userGroupsDrawerOpen, setUserGroupsDrawerOpen] = useState<boolean>(false);
  const [userGroupsDrawerTab, setUserGroupsDrawerTab] = useState<string>("groups");
  const [userGroupRequestsDrawerOpen, setUserGroupRequestsDrawerOpen] = useState<boolean>(false);
  const [moduleUserGroupsMap, setModuleUserGroupsMap] = useState<Record<number, { userGroupID: number; userGroupName: string }[]>>({});
  

  // Redirect legacy hash-based URLs (e.g. /#/placementRequest) to real paths
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith("#/")) {
      const hashPath = hash.slice(1); // strip the leading #
      navigate(hashPath, { replace: true });
    }
  }, []);

  // Wait for user to be loaded from localStorage
  useEffect(() => {
    // Give AppContext time to load user from localStorage
    const timer = setTimeout(() => setIsLoading(false), 200);
    setPathForTab();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if(user && user.organization && user.organization.organizationTypeID == 2){
      fetchRequestTasks();
    }
  }, [user]);

  const fetchRequestTasks = async () => {
    try{
      // getUserTenderingAccessTasks only returns unanswered requests, so fetch from the
      // paginated task endpoint with getCompletedTasks=true to include answered ones too.
      const response = await getTasks(true, 1000, 0, TENDERING_ACCESS_REQUEST_TASK_TYPE_ID);
      // Defensive filter: only ever use the current user's own requests
      const tasks = (response?.tasks ?? []).filter((t: TaskListItem) => t.taskIssuedByUserID === user.user.userID);
      setRequestTasks(tasks);
    }catch(error){
      console.error("Error fetching tasks:", error);
    }
  }

  // Requests that are still awaiting an answer from an administrator (isTaskApproved is
  // null until someone accepts or denies it). Approved/denied requests are resolved and
  // shouldn't keep blocking or cluttering the "not yet answered" view.
  const unansweredRequestTasks = useMemo(() => {
    return requestTasks.filter(t => t.isTaskApproved === null || t.isTaskApproved === undefined);
  }, [requestTasks]);

  // Requests the user made that have since received an answer (approved or denied).
  const answeredRequestTasks = useMemo(() => {
    return requestTasks.filter(t => t.isTaskApproved === true || t.isTaskApproved === false);
  }, [requestTasks]);

  const parseTaskData = (t: TaskListItem): TaskData => {
    try {
      return typeof t.taskData === 'string' ? JSON.parse(t.taskData) as TaskData : t.taskData as TaskData;
    } catch (error) {
      console.error("Error parsing task data:", error);
      return {};
    }
  }

  // Answered requests the user hasn't opened the Ansökningar drawer for yet. Read status is
  // persisted server-side (taskData.IsNotificationRead), set via markTaskNotificationRead
  // when the drawer is opened, so it stays in sync across devices/sessions.
  const unseenAnsweredRequestsCount = useMemo(() => {
    return answeredRequestTasks.filter(t => {
      const data = parseTaskData(t);
      const isRead = data.IsNotificationRead ?? data.isNotificationRead;
      return !isRead;
    }).length;
  }, [answeredRequestTasks]);

  const getTaskUserGroupID = (t: TaskListItem): number | undefined => {
    try {
      const data = typeof t.taskData === 'string' ? JSON.parse(t.taskData) as TaskData : t.taskData as TaskData;
      return data?.TaskUserGroupID ?? data?.taskUserGroupID;
    } catch (error) {
      console.error("Error parsing task data for request task:", error);
      return undefined;
    }
  }

  // Missing RequestType means the task predates this field and was always a join request; backend sends the enum as a number (0 = Join, 1 = Leave)
  const getTaskRequestType = (t: TaskListItem): 'Join' | 'Leave' => {
    try {
      const data = typeof t.taskData === 'string' ? JSON.parse(t.taskData) as TaskData : t.taskData as TaskData;
      const raw = data?.RequestType ?? data?.requestType;
      return raw === 1 || raw === 'Leave' ? 'Leave' : 'Join';
    } catch (error) {
      console.error("Error parsing task data for request task:", error);
      return 'Join';
    }
  }

  // A user can request access once per user group, so we need to know which
  // user group (if any) each unanswered request task belongs to, keyed by tendering moduleID.
  const requestedUserGroupsByModule = useMemo(() => {
    const map: Record<number, Set<number>> = {};

    unansweredRequestTasks.filter(t => getTaskRequestType(t) === 'Join').forEach(t => {
      const userGroupID = getTaskUserGroupID(t);

      if (!map[t.taskModuleID]) {
        map[t.taskModuleID] = new Set<number>();
      }
      // Requests without a user group (e.g. tendering has none) fall back to a single marker (-1)
      map[t.taskModuleID].add(userGroupID ?? -1);
    });

    return map;
  }, [unansweredRequestTasks]);

  useEffect(() => {
    if (user && user.organization && user.organization.organizationTypeID == 2 && tenderingModules.length > 0) {
      fetchModuleUserGroups();
    }
  }, [user, tenderingModules, tenderModules]);

  const fetchModuleUserGroups = async () => {
    const candidateModules = tenderingModules.filter(t => getTenderModulesByTendering(t.moduleID).length === 0 && !t.tenderListHidden);
    if (candidateModules.length === 0) return;

    try {
      const entries = await Promise.all(
        candidateModules.map(async (t) => {
          try {
            const groups = await getUserGroupsByModuleAndOrganizationID(t.moduleID, user.organization.organizationID);
            return [t.moduleID, groups ?? []] as const;
          } catch (error) {
            console.error(`Error fetching user groups for module ${t.moduleID}:`, error);
            return [t.moduleID, []] as const;
          }
        })
      );
      setModuleUserGroupsMap(prev => ({ ...prev, ...Object.fromEntries(entries) }));
    } catch (error) {
      console.error("Error fetching module user groups:", error);
    }
  }

  // Update actualTab based on current route
  useEffect(() => {
    setPathForTab();
  }, [location.pathname]);

  const setPathForTab = () => {
    const path = location.pathname;
    let currentTab: TabValue = 'tab1';
    
    if (path === '/' || path === '/start') {
      currentTab = 'tab1';
    } else if (path.startsWith('/ramavtal')) {
      currentTab = 'tab2';
    } else if (path.startsWith('/forfragningar') || path.startsWith('/forfragan')) {
      currentTab = 'tab3';
    } else if (path === '/andringsbegaran') {
      currentTab = 'tab4';
    } else if (path === '/avtalsadministrativ') {
      currentTab = 'tab5';
    } else if (path === '/avtalsuppfoljning') {
      currentTab = 'tab6';
    } else if (path === '/loggboksverktyg') {
      currentTab = 'tab8';
    }
    
    setActualTab(currentTab);
  }


  useEffect(() =>{
    if(user){
      setTenderingModules(user.modules.filter((m:Module) => m.moduleType == 'Tendering'));
      setTenderModules(user.modules.filter((m:Module) => m.moduleType == 'Tender'));
    }
  }, [user]);

  const getListPrio = (module: Module): number => {
    const parentModuleID = +module.modulePath.split('_')[2];

    const isArchived = tenderingModules.find(m => m.moduleID === parentModuleID)?.moduleName.includes('Avpublicerad') || tenderingModules.find(m => m.moduleID === parentModuleID)?.moduleName.includes('Arkiverad');

    if(module.moduleType === 'Tendering'){
      return isArchived ? tenderingModules.indexOf(module) + 1000 : tenderingModules.indexOf(module);
    }
    
    return 9999;
  }

  // Handle moduleID from URL query parameter
  useEffect(() => {
    const moduleID = searchParams.get('moduleID');
    if (moduleID && tenderingModules.length > 0) {
      const module = tenderingModules.find(m => m.moduleID.toString() === moduleID);
      if (module) {
        setSelectedModule(module);
      }
    }
  }, [searchParams, tenderingModules]);
  
  const onTabChange = (tabValue: TabValue) => {
    if (tabValue === 'tab2') return;
    if (tabValue === 'tab7') return;
    if (tabValue === 'tab9') return;
    if (tabValue === 'tab10') return;
    
    const tabToPath = {
      'tab1': '/start',
      'tab2': '/ramavtal',
      'tab3': '/forfragningar',
      'tab4': '/andringsbegaran',
      'tab5': '/avtalsadministrativ',
      'tab6': '/avtalsuppfoljning',
      'tab8': '/loggboksverktyg',
    };
    
    setActualTab(tabValue);
    navigate(tabToPath[tabValue]);
  }

  const getTenderModulesByTendering = (moduleID: number): Module[] => {
    return tenderModules.filter(tm => +tm.modulePath.split('_')[2] === moduleID).sort((a, b) => a.moduleName.localeCompare(b.moduleName));
  }

  const handleTenderModuleClick = (tm: Module) => {
    setSelectedTenderModule(tm);
    setOpenTenderSettingsDrawer(true);
    setMenuOpen({});
  }

  const handleTenderingRequest = async (t: Module) => {
    setPendingTenderingRequest(t);
    setSelectedRequestUserGroupIDs([]);
    setTenderingRequestUserGroups([]);
    setRequestUserGroupAdminsMap({});
    setLoadingRequestUserGroupAdminIDs([]);

    try {
      const userGroups = await getUserGroupsByModuleAndOrganizationID(t.moduleID, user.organization.organizationID);
      // Only offer user groups the user hasn't already requested access to
      const requestedGroupIDs = requestedUserGroupsByModule[t.moduleID] ?? new Set<number>();
      const availableUserGroups = (userGroups ?? []).filter((ug: { userGroupID: number }) => !requestedGroupIDs.has(ug.userGroupID));
      setTenderingRequestUserGroups(availableUserGroups);
    }
    catch (error) {
      console.error("Error fetching user groups for tendering request:", error);
      setTenderingRequestUserGroups([]);
    }

    setIsConfirmationDialogOpen(true);
  }

  const handleSelectRequestUserGroups = async (userGroupIDs: string[]) => {
    setSelectedRequestUserGroupIDs(userGroupIDs);

    if (!pendingTenderingRequest) return;

    // Only fetch admins for newly selected groups that haven't been fetched yet
    const newUserGroupIDs = userGroupIDs.filter(
      (id) => !(id in requestUserGroupAdminsMap) && !loadingRequestUserGroupAdminIDs.includes(id)
    );
    if (newUserGroupIDs.length === 0) return;

    setLoadingRequestUserGroupAdminIDs(prev => [...prev, ...newUserGroupIDs]);

    await Promise.all(newUserGroupIDs.map(async (userGroupID) => {
      try {
        const admins = await getUsersByModuleAndRole(pendingTenderingRequest.moduleID, "Behörighetsansvarig", +userGroupID);
        setRequestUserGroupAdminsMap(prev => ({ ...prev, [userGroupID]: admins ?? [] }));
      }
      catch (error) {
        console.error("Error fetching behörighetsansvariga:", error);
        setRequestUserGroupAdminsMap(prev => ({ ...prev, [userGroupID]: [] }));
      }
      finally {
        setLoadingRequestUserGroupAdminIDs(prev => prev.filter(id => id !== userGroupID));
      }
    }));
  }

  const confirmTenderingRequest = async () => {
    if (!pendingTenderingRequest) return;
    if (tenderingRequestUserGroups.length === 0 || selectedRequestUserGroupIDs.length === 0) return;

    const selectedUserGroups = tenderingRequestUserGroups.filter(
      ug => selectedRequestUserGroupIDs.includes(ug.userGroupID.toString())
    );

    const tasks: CreateTask[] = selectedUserGroups.map(selectedUserGroup => ({
      taskName: `Begäran om att gå med i ramavtalet ${pendingTenderingRequest.moduleName} för användare ${user.user.email}`,
      taskDescription: `Användare ${user.user.email} har begärt att gå med i ramavtalet ${pendingTenderingRequest.moduleName} i gruppen ${selectedUserGroup.userGroupName}.\n\nVänligen granska och godkänn eller avslå denna begäran.`,
      taskData: {
        taskModuleID: pendingTenderingRequest.moduleID,
        taskOrganizationID: user.organization.organizationID,
        userEmail: user.user.email,
        taskUserGroupID: selectedUserGroup.userGroupID,
      }
    }));

    try{
      const results = await Promise.all(tasks.map(task => createTenderingRequestTask(task)));
      const sentCount = results.filter(r => r.taskID !== 0).length;
      const alreadySentCount = results.length - sentCount;

      if (sentCount > 0 && alreadySentCount === 0) {
        setNotificationText("En begäran om att gå med i ramavtalet har skickats till behörighetsansvariga.");
        setIsNotificationDialogOpen(true);
      }
      else if (sentCount > 0 && alreadySentCount > 0) {
        setNotificationText("En begäran om att gå med i ramavtalet har skickats för de valda grupperna. För en eller flera av grupperna har en begäran redan skickats tidigare och väntar på godkännande.");
        setIsNotificationDialogOpen(true);
      }
      else {
        setNotificationText("En begäran om att gå med i ramavtalet har redan skickats tidigare och väntar på godkännande.");
        setIsNotificationDialogOpen(true);
      }
    }
    catch(error){
      console.error("Error creating tendering request task:", error);
      setIsNotificationDialogOpen(false);
      setErrorMessage("Det finns inga grupper i ramavtalet, eller så har ett annat fel uppstått. Kontakta ramavtalsansvarig för att bli tillagd");
      setIsErrorDialogOpen(true);
    }
    finally{
      fetchRequestTasks();
      setIsConfirmationDialogOpen(false);
      setPendingTenderingRequest(undefined);
      setTenderingRequestUserGroups([]);
      setSelectedRequestUserGroupIDs([]);
      setRequestUserGroupAdminsMap({});
      setLoadingRequestUserGroupAdminIDs([]);
    }
  }


  return (
    <>
      {user && <div className="flex items-center justify-center shadow-md shadow-gray-400 z-10 top-11 bg-white h-11">
        <div className="w-[1480px] flex gap-4 items-center">
          <TabList
            disabled = {!user}
            selectedValue={actualTab}
            onTabSelect={(_, data) => onTabChange(data.value as TabValue)}
          >
            <Tab
              value="tab1"
              icon={<HomeIcon />}
            >
              <p>Start</p>
            </Tab>
            <Menu
              open={tenderingMenuOpen}
              onOpenChange={(e, data) => setTenderingMenuOpen(data.open)}
              openOnHover
              hoverDelay={1}
            >
              <MenuTrigger disableButtonEnhancement>
                <Tab
                  value="tab2"
                  icon={<DocumentBulletListIcon />}
                  onClick={(e) => e.preventDefault()}
                >
                  <p>Ramavtal</p>
                </Tab>
              </MenuTrigger>
              <MenuPopover>
                <MenuList style={{ maxHeight: '80vh', overflowX: 'hidden', overflowY: 'auto' }}>
                    <>
                      {(() => {
                        // A tendering is only fully "requested" (and blocked from new requests)
                        // once the user has a pending request for every one of its user groups.
                        const isModuleFullyRequested = (t: Module): boolean => {
                          const requestedGroupIDs = requestedUserGroupsByModule[t.moduleID];
                          if (!requestedGroupIDs || requestedGroupIDs.size === 0) return false;

                          const totalGroups = moduleUserGroupsMap[t.moduleID]?.length ?? 0;
                          // No user groups exist for this tendering - a single pending request blocks new ones
                          if (totalGroups === 0) return true;

                          return requestedGroupIDs.size >= totalGroups;
                        };

                        const modulesWithTenders = tenderingModules.filter(t => 
                          getTenderModulesByTendering(t.moduleID).length > 0 || t.tenderListHidden
                        );
                        const modulesWithPendingRequests = tenderingModules.filter(t => 
                          getTenderModulesByTendering(t.moduleID).length === 0 &&
                          !t.tenderListHidden && isModuleFullyRequested(t)
                        );
                        const modulesWithoutTenders = tenderingModules.filter(t => 
                          getTenderModulesByTendering(t.moduleID).length === 0 &&
                          !t.tenderListHidden && !isModuleFullyRequested(t)
                        );

                        return (
                          <>
                            {modulesWithTenders.length > 0 && (
                              <MenuGroup>
                                <MenuGroupHeader>Mina tillgängliga ramavtal</MenuGroupHeader>
                                {modulesWithTenders.map(t => {
                                  const tenderModulesForTendering = getTenderModulesByTendering(t.moduleID);

                                  const isSupplier = user.roles.some((r: RoleDTO) => r.roleType === 'Supplier');

                                  const hasVerksamhetslistaPermission = featurePermissions?.some(
                                    fp => fp.action === 'View supplier list'
                                  );

                                  // tenderListHidden blocks everyone including suppliers
                                  const showSubmenu = !t.tenderListHidden && tenderModulesForTendering.length > 0
                                    && (hasVerksamhetslistaPermission || isSupplier);

                                  if (tenderModulesForTendering.length === 0 || !showSubmenu) {
                                    return (
                                      <MenuItem
                                        key={t.moduleID}
                                        onClick={() => {
                                          if(hasTenderingPageAccess(t.moduleID)){
                                            setTenderingMenuOpen(false);
                                            setActualTab('tab2');
                                            navigate(`/ramavtal?moduleID=${t.moduleID}`);
                                            setSelectedModule(t);
                                          }
                                          else{
                                            //Cant navigate if there is no permission
                                          }
                                        }}
                                      >
                                        {t.moduleName}
                                      </MenuItem>
                                    );
                                  }

                                  return (
                                    <Menu
                                      key={t.moduleID}
                                      open={menuOpen[t.moduleID] === true}
                                      onOpenChange={(e, data) => setMenuOpen(prev => ({ ...prev, [t.moduleID]: data.open }))}
                                    >
                                      <MenuTrigger>
                                        <MenuItem 
                                        onClick={() => {
                                          if(hasTenderingPageAccess(t.moduleID)){
                                            setTenderingMenuOpen(false);
                                            setActualTab('tab2');
                                            navigate(`/ramavtal?moduleID=${t.moduleID}`);
                                            setSelectedModule(t);
                                          }
                                          else{
                                            //Cant navigate if there is no permission
                                          }
                                        }}
                                        >{t.moduleName}</MenuItem>
                                      </MenuTrigger>
                                      <MenuPopover>
                                        <MenuList style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                          {tenderModulesForTendering.map(tm => (
                                            <MenuItem key={tm.moduleID} onClick={() => handleTenderModuleClick(tm)}>{tm.moduleName}</MenuItem>
                                          ))}
                                        </MenuList>
                                      </MenuPopover>
                                    </Menu>
                                  );
                                })}
                              </MenuGroup>
                            )}
                            
                            {(modulesWithTenders.length > 0 || modulesWithPendingRequests.length > 0) && modulesWithPendingRequests.length > 0 && (
                              <>
                                <MenuDivider />
                                <MenuGroupHeader>Väntande ansökningar</MenuGroupHeader>
                              </>
                            )}
                            
                            {modulesWithPendingRequests.length > 0 && (
                              <MenuGroup>
                                {modulesWithPendingRequests.map(t => (
                                  <MenuItem key={t.moduleID} disabled>{t.moduleName}</MenuItem>
                                ))}
                              </MenuGroup>
                            )}
                            
                            {modulesWithoutTenders.length > 0 && (
                              <>
                                {(modulesWithTenders.length > 0 || modulesWithPendingRequests.length > 0) && <MenuDivider />}
                                <MenuGroupHeader>Ansök om tillgång till ramavtal</MenuGroupHeader>
                                <MenuGroup>
                                  {modulesWithoutTenders.map(t => (
                                    <MenuItem key={t.moduleID} onClick={() => {
                                      handleTenderingRequest(t);
                                    }}>{t.moduleName}</MenuItem>
                                  ))}
                                </MenuGroup>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </>
                </MenuList>
              </MenuPopover>
            </Menu>
            {featurePermissions?.some(fp => fp.action == 'Placement') && <Tab
              value="tab3"
              icon={<BriefcaseSearchIcon />}
            >
              <p>Förfrågningar</p>
            </Tab>}
            {user && user.roles.some((r: RoleDTO) => r.roleType == 'General') && <Tab
              value="tab4"
              icon={<WindowShieldIcon />}
            >
              <p>Ändringsbegäran</p>
            </Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering fee tool page') && <Tab
              value="tab5"
              icon={<ClipboardDataBarIcon />}
            >
              <p>Avtalsadministrativ avgift</p>
            </Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering manager tool page') && <Tab
              value="tab6"
              icon={<ClipboardDataBarIcon />}
            >
              <p>Avtalsuppföljning</p>
            </Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tender page/Notes') && <Tab
              value="tab8"
              icon={<NoteBookIcon />}
            >
              <p>Loggböcker</p>
            </Tab>}
            {user && user.organization && user.organization.organizationTypeID == 2 && <div className="relative">
              <Tab
                value="tab9"
                onClick={(e) => {setUserGroupRequestsDrawerOpen(true);}}
                icon={<ListBarTreeOffsetRegular />}
              >
                <p>Ansökningar</p>
              </Tab>
              {unseenAnsweredRequestsCount > 0 && (
                <CounterBadge showZero={false} style={{position:"absolute", right:"0px", top:"5px"}} size="small" title="Antal ansökningar med nytt svar">{unseenAnsweredRequestsCount}</CounterBadge>
              )}
            </div>}
            {featurePermissions?.some(p => p.action == 'See users user groups') && user && user.roles.some((role: RoleDTO) => role.roleType == "General" || role.roleType == "Procurer") && <Tab
              value="tab10"
              onClick={(e) => {setUserGroupsDrawerTab("groups"); setUserGroupsDrawerOpen(true);}}
              icon={<PeopleAudienceRegular />}
            >
              <p>Grupper</p>
            </Tab>}
            {(userTasks.length > 0) && user && (
              user.roles.some((r: RoleDTO) => r.roleType == 'General')
              || user.roles.some((r: RoleDTO) => r.roleName == 'Ramavtalsansvarig')
              || user.roles.some((r: RoleDTO) => r.roleName == 'Behörighetsansvarig')
              || user.roles.some((r: RoleDTO) => r.roleName == 'Kontoadmin')
            ) && <div className="relative">
              <Tab
                value="tab7"
                onClick={(e) => {setUserGroupsDrawerTab("requests"); setUserGroupsDrawerOpen(true);}}
                icon={<PeopleAudienceRegular />}
              >
                <p>Gruppbegäran</p>
              </Tab>
              <CounterBadge showZero={false} style={{position:"absolute", right:"0px", top:"5px"}} size="small" title="Antal gruppbegäran">{userTasks.length}</CounterBadge>
            </div>}
          </TabList>
        </div>
      </div>}
      <div>
        {isLoading ? (
          <div className="flex justify-center items-center h-screen">
            <div>Loading...</div>
          </div>
        ) : user ? (
          <Routes>
            <Route path="/" element={<Navigate to="/start" replace />} />
            <Route path="/login" element={<Navigate to="/start" replace />} />
            <Route path="/start" element={<Start />} />
            <Route path="/ramavtal" element={selectedModule ? <Tendering tenderingModule={selectedModule} /> : <div>Välj ett ramavtal</div>} />
            <Route path="/forfragan" element={<PlacementRequestComponent />} />
            <Route path="/forfragningar" element={<Placement />} />
            <Route path="/forfragningar/:id" element={<PlacementComponent />} />
            <Route path="/andringsbegaran" element={<TenderChanges />} />
            <Route path="/avtalsadministrativ" element={<TenderingFeeTool />} />
            <Route path="/avtalsuppfoljning" element={<TenderingManagerTool />} />
            <Route path="/loggboksverktyg" element={<LogsTool />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="*" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forfragan" element={<PlacementRequestComponent />} />
            <Route path="/placementRequest" element={<PlacementRequestComponent />} />
            <Route path="/account-request" element={<AccountRequest />} />
            <Route path="/reset-password" element={<UserReset />} />
          </Routes>
        )}
      </div>
      <TenderSettingsDrawer 
        open={openTenderSettingsDrawer}
        tab="info"
        onClose={() => {setOpenTenderSettingsDrawer(false); setSelectedTenderModule(undefined);}}
        partialTender={selectedTenderModule}
      />
      <TenderingRequestDialog
        isOpen={isConfirmationDialogOpen}
        tenderingName={pendingTenderingRequest?.moduleName}
        userGroups={tenderingRequestUserGroups}
        selectedUserGroupIDs={selectedRequestUserGroupIDs}
        onSelectUserGroups={handleSelectRequestUserGroups}
        groupAdminsByUserGroupID={requestUserGroupAdminsMap}
        loadingGroupAdminIDs={loadingRequestUserGroupAdminIDs}
        onConfirm={confirmTenderingRequest}
        onClose={() => {
          setIsConfirmationDialogOpen(false);
          setPendingTenderingRequest(undefined);
          setTenderingRequestUserGroups([]);
          setSelectedRequestUserGroupIDs([]);
          setRequestUserGroupAdminsMap({});
          setLoadingRequestUserGroupAdminIDs([]);
        }}
      />
      <NotificationDialog isOpen={isNotificationDialogOpen} message={notificationText} onClose={() => setIsNotificationDialogOpen(false)} />
      <ErrorDialog isOpen={isErrorDialogOpen} message={errorMessage} onClose={() => setIsErrorDialogOpen(false)} />
      <UserGroupsDrawer open={userGroupsDrawerOpen} onClose={() => setUserGroupsDrawerOpen(false)} tab={userGroupsDrawerTab} />
      <UserGroupRequests open={userGroupRequestsDrawerOpen} onClose={() => setUserGroupRequestsDrawerOpen(false)} onRequestsUpdated={fetchRequestTasks} />
      {/* {user && <SessionWarningBanner />} */}
    </>
  );
};
export default Main;
